import React from 'react';
import { usersApi, type UserSecurityEvent } from '../../../api/users';
import {
  EmptyState,
  Muted,
  RowCard,
  StatusPill,
  TabLoader,
  formatDateTime,
  useUserTabData,
  type Tone,
} from './userTabShared';

interface Props {
  userId: string;
  isDark: boolean;
}

const EVENT_LABEL: Record<string, string> = {
  LOGIN_SUCCESS: 'Вход',
  LOGIN_FAILED: 'Неудачный вход',
  LOGOUT: 'Выход',
  SESSION_CREATED: 'Новая сессия',
  SESSION_REVOKED: 'Сессия отозвана',
  DEVICE_LIMIT_EXCEEDED: 'Превышен лимит устройств',
  GEO_ANOMALY: 'Гео-аномалия',
  RAPID_LOCATION_CHANGE: 'Быстрая смена локации',
  SUSPICIOUS_IP: 'Подозрительный IP',
  RATE_LIMIT_EXCEEDED: 'Слишком много запросов',
  TOKEN_REUSE: 'Повтор токена',
  CONCURRENT_SESSIONS: 'Параллельные сессии',
  ACCOUNT_SHARING_SUSPECTED: 'Подозрение на шеринг аккаунта',
  TIME_MANIPULATION: 'Подмена времени',
  DEVICE_MISMATCH: 'Несовпадение устройства',
  DUPLICATE_MERGED: 'Склейка дубля',
};

const METHOD_LABEL: Record<string, string> = {
  password: 'пароль',
  google: 'Google',
  apple: 'Apple',
  anonymous: 'аноним',
  email: 'email-код',
  telegram: 'Telegram',
};

const SEVERITY_TONE: Record<UserSecurityEvent['severity'], Tone> = {
  INFO: 'blue',
  WARNING: 'amber',
  CRITICAL: 'red',
};

export function UserSecurityTab({ userId, isDark }: Props) {
  const { data, loading } = useUserTabData<{ items: UserSecurityEvent[] }>(
    userId,
    usersApi.getUserSecurityLog,
    'Не удалось загрузить журнал',
  );

  if (loading) return <TabLoader />;
  if (!data || data.items.length === 0) return <EmptyState isDark={isDark}>Событий нет.</EmptyState>;

  return (
    <div className="space-y-2">
      <Muted isDark={isDark} className="block mb-2">
        Последние {data.items.length} событий: входы, выходы, срабатывания защиты.
      </Muted>
      {data.items.map((e) => (
        <RowCard key={e.id} isDark={isDark} className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-0.5">
            <div className="text-sm font-semibold">
              {EVENT_LABEL[e.eventType] ?? e.eventType}
              {e.loginMethod && (
                <span className="font-normal"> · {METHOD_LABEL[e.loginMethod] ?? e.loginMethod}</span>
              )}
            </div>
            <Muted isDark={isDark} className="block">
              {formatDateTime(e.createdAt)}
              {e.ipAddress && ` · IP ${e.ipAddress}`}
              {e.country && ` · ${e.country}`}
            </Muted>
            {(e.failureReason || e.message) && (
              <Muted isDark={isDark} className="block">
                {e.failureReason || e.message}
              </Muted>
            )}
          </div>
          <StatusPill tone={SEVERITY_TONE[e.severity] ?? 'slate'}>{e.severity}</StatusPill>
        </RowCard>
      ))}
    </div>
  );
}
